import { useState, useCallback } from 'react';
import { ApiError, ErrorType } from '../types/errors';

interface UseErrorHandlerReturn {
  error: ApiError | null;
  setError: (error: ApiError | null) => void;
  clearError: () => void;
  getErrorMessage: () => string;
  isRetryable: () => boolean;
  getErrorType: () => ErrorType | null;
}

export const useErrorHandler = (): UseErrorHandlerReturn => {
  const [error, setError] = useState<ApiError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getErrorMessage = useCallback((): string => {
    if (!error) return '';
    
    // Return user-friendly message based on error type
    switch (error.type) {
      case ErrorType.NETWORK:
        return 'Connection problem. Please check your internet and try again.';
      case ErrorType.AUTHENTICATION:
        return 'Please log in to continue.';
      case ErrorType.AUTHORIZATION:
        return 'You don\'t have permission for this action.';
      case ErrorType.VALIDATION:
        if (error.validationErrors && error.validationErrors.length > 0) {
          return error.validationErrors.map(err => err.message).join(', ');
        }
        return error.message;
      case ErrorType.RATE_LIMIT:
        return 'Too many requests. Please wait a moment and try again.';
      case ErrorType.SERVER:
        return 'Server problem. Please try again later.';
      default:
        return error.message;
    }
  }, [error]);

  const isRetryable = useCallback((): boolean => {
    return error?.retryable ?? false;
  }, [error]);

  const getErrorType = useCallback((): ErrorType | null => {
    return error?.type ?? null;
  }, [error]);

  return {
    error,
    setError,
    clearError,
    getErrorMessage,
    isRetryable,
    getErrorType,
  };
};

export default useErrorHandler;
